import * as React from "react";
import { useCallback, useState, useMemo } from "react";
import { useSocialCoachSession, getPlayerAccountFromSession, useCanImpersonate, useImpersonation } from "@/utils";
import { Sidebar } from "@/components/ui/sidebar";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { LogOut, UserCog, Search, ShieldKeyhole, CornerUpLeft } from "lucide-react";
import { UserSettingsDialog } from "../Modals";
import PlayerEditProfileDialog from "@/containers/Players/PlayerSettings/components/PlayerEditProfileDialog";

interface SideBarFooterProps {
  history: any;
  logoutRedirectPath: string;
}

export const SideBarFooter: React.FC<SideBarFooterProps> = ({ history, logoutRedirectPath }) => {
  const { session, fullName, isPlayer, logout, refreshSession, isImpersonating } = useSocialCoachSession();
  const { revert } = useImpersonation();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<"profile" | "security">("profile");
  const canImpersonate = useCanImpersonate();

  React.useEffect(() => {
    const handleOpenSettings = (e: Event) => {
      const customEvent = e as CustomEvent<{ tab?: "profile" | "security" }>;
      const tab = customEvent.detail?.tab || "profile";
      setSettingsTab(tab);
      setIsSettingsOpen(true);
    };

    window.addEventListener("open-global-user-settings", handleOpenSettings);
    return () => {
      window.removeEventListener("open-global-user-settings", handleOpenSettings);
    };
  }, []);

  const isMac = useMemo(() => {
    if (typeof window === "undefined") return true;
    return /Macintosh|Mac OS X/i.test(navigator.userAgent);
  }, []);

  const playerAccount = useMemo(() => getPlayerAccountFromSession(session), [session]);

  const handleLogout = useCallback(async () => {
    setIsLogoutDialogOpen(false);
    await logout();
    history.push(logoutRedirectPath);
  }, [history, logoutRedirectPath, logout]);

  const getInitials = (name?: string) => {
    if (!name) return "SC";
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Sidebar.Footer className="mt-auto border-t border-slate-200/80 bg-slate-100/40 px-3 py-4">
      <div className="flex flex-col gap-3">
        {canImpersonate && (
          <Button
            variant="outline"
            size="sm"
            className="w-full h-8 justify-start text-[11px] gap-2 border-slate-200 bg-white text-slate-500 hover:text-slate-800 hover:bg-slate-50/50 shadow-none px-2 font-semibold"
            onClick={() => window.dispatchEvent(new CustomEvent("open-global-impersonation-switch"))}
            title={`Search and switch to another account (${isMac ? "⌘K" : "Ctrl+K"})`}
          >
            <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span>Switch Accounts</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-4.5 select-none items-center gap-0.5 rounded border bg-slate-50 px-1 font-mono text-[10px] font-bold text-slate-400 opacity-100">
              {isMac ? (
                <>
                  <span className="text-xs">⌘</span>K
                </>
              ) : (
                <>Ctrl+K</>
              )}
            </kbd>
          </Button>
        )}

        <div className="flex items-center gap-3 px-1">
          <Avatar className="h-9 w-9 shrink-0 border border-slate-200 shadow-sm">
            <Avatar.Image src={session?.photoUrl} alt={fullName || ""} />
            <Avatar.Fallback className="bg-white text-indigo-600 font-bold text-[10px]">
              {getInitials(fullName)}
            </Avatar.Fallback>
          </Avatar>
          <div className="flex flex-col min-w-0 leading-tight">
            <span className="text-[13px] font-bold text-slate-700 truncate flex items-center gap-1.5" title={fullName}>
              <span>{fullName}</span>
              {session?.mfaVerified && !session?.actor && (
                <ShieldKeyhole className="h-3.5 w-3.5 text-slate-700 shrink-0" title="MFA Verified Session" />
              )}
            </span>
            <span className="text-[11px] text-slate-400 truncate" title={session?.email}>
              {session?.email}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-[11px] gap-1.5 px-2 text-slate-500 hover:bg-white hover:text-slate-900 transition-all font-semibold"
            onClick={() => {
              setSettingsTab("profile");
              setIsSettingsOpen(true);
            }}
          >
            <UserCog className="h-3.5 w-3.5" />
            <span>My Account</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-[11px] gap-1.5 px-2 text-rose-400 hover:bg-rose-50 hover:text-rose-600 transition-all font-semibold"
            onClick={() => setIsLogoutDialogOpen(true)}
          >
            <LogOut className="h-3.5 w-3.5" />
            <span>Sign Out</span>
          </Button>
        </div>
      </div>

      {isPlayer ? (
        playerAccount && (
          <PlayerEditProfileDialog
            open={isSettingsOpen}
            onOpenChange={setIsSettingsOpen}
            playerAccount={playerAccount}
            onSuccess={refreshSession}
          />
        )
      ) : (
        <UserSettingsDialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen} defaultTab={settingsTab} />
      )}

      <Dialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen} size="sm">
        <Dialog.Header>
          <Dialog.Title>
            {isImpersonating ? `Finish Helping ${fullName || "User"}?` : "Ready to Sign Out?"}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Content>
          {isImpersonating ? (
            <p className="text-slate-600 leading-normal">
              You are currently viewing the workspace as{" "}
              <span className="font-semibold text-slate-900">{fullName}</span>. Would you like to switch back to your
              account or sign out of SocialCoach completely?
            </p>
          ) : (
            <p className="text-slate-600 leading-normal">
              Are you sure you want to sign out of your session? We'll keep everything ready for when you return.
            </p>
          )}
        </Dialog.Content>
        <Dialog.Footer className="flex flex-col gap-2 p-4 bg-slate-50 border-t border-slate-100 rounded-b-xl w-full sm:flex-col sm:items-stretch sm:justify-start">
          {isImpersonating ? (
            <>
              <Button
                variant="default"
                className="w-full justify-center gap-2 h-10 font-semibold"
                onClick={async () => {
                  setIsLogoutDialogOpen(false);
                  await revert();
                }}
              >
                <CornerUpLeft className="h-4 w-4" />
                <span>Switch Back to {session?.actor?.fullName || session?.actor?.email || "your account"}</span>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-center gap-2 h-10 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 font-semibold"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out Completely</span>
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-center h-10 text-slate-500 hover:bg-slate-100 hover:text-slate-800 font-semibold"
                onClick={() => setIsLogoutDialogOpen(false)}
              >
                Keep Helping
              </Button>
            </>
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <Button
                variant="destructive"
                className="w-full justify-center gap-2 h-10 font-semibold"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-center h-10 text-slate-600 border-slate-200 hover:bg-slate-50 font-semibold"
                onClick={() => setIsLogoutDialogOpen(false)}
              >
                Stay Signed In
              </Button>
            </div>
          )}
        </Dialog.Footer>
      </Dialog>
    </Sidebar.Footer>
  );
};
